import React from 'react';
import { useNavigate } from 'react-router-dom';

const AITwinProject = () => {
  const navigate = useNavigate();
  return (
    <div className="bg-black pt-24 pb-16">
      <div className="max-w-4xl mx-auto px-6">

        {/* Back button */}
        <button 
          onClick={() => navigate('/blog')} 
          className="mb-8 flex items-center text-gray-400 hover:text-white transition"
        >
          <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
          </svg>
          Back to Blog
        </button>

        {/* Blog post header */}
        <header className="mb-10">
          <h1 className="text-4xl font-extrabold text-white mb-4">
            The Human AI Twin Project: Pioneering the Next Generation of Market Research
          </h1>
          <div className="flex items-center text-sm text-gray-400 mb-6">
            <span>January 2025</span>
            <span className="mx-2">•</span>
            <span>6 min read</span>
            <span className="mx-2">•</span>
            <span>Product Innovation</span>
          </div>
          <div className="flex items-center">
            <img 
              src={process.env.PUBLIC_URL + '/img/sharif-headshot.jpg'} 
              alt="Dr. Sharif Amlani" 
              className="h-10 w-10 rounded-full mr-3"
            />
            <div>
              <p className="text-white font-medium">Dr. Sharif Amlani</p>
              <p className="text-gray-400 text-sm">Founder & CEO, HumanAI</p>
            </div>
          </div>
        </header>

        {/* Hero Image - Meet Alex */}
        <div className="mb-8">
          <img 
            src={process.env.PUBLIC_URL + '/img/AI Twin Project/ChatGPT Image Jul 11, 2025, 06_36_18 PM.png'}
            alt="Meet Alex - AI Twin Project Introduction"
            className="w-full rounded-lg shadow-lg"
          />
        </div>

        {/* Blog content */}
        <div className="prose prose-lg prose-invert max-w-none space-y-6">

          <p className="text-lg">
            HumanAI's AI Twin Project is pioneering the next generation of market and opinion research by creating AI agents—"twins"—that accurately reflect the attitudes, beliefs, and behaviors of real individuals.
          </p>

          <p>
            Inspired by the groundbreaking Stanford study, "Building and Auditing Machine Learning Models for Predicting Survey Responses", we follow a similar, evidence-based approach. Each AI Twin is developed through an in-depth onboarding interview with a real person, rigorous human-in-the-loop validation, and continuous updates to ensure our models stay current as people's attitudes evolve.
          </p>

          <h2 className="text-2xl font-bold mt-8 mb-4">How It Works: The AI Twin Journey</h2>

          {/* Step 1: Long-form Interview */}
          <div className="mb-8">
            <img 
              src={process.env.PUBLIC_URL + '/img/AI Twin Project/ChatGPT Image Jul 11, 2025, 06_36_36 PM.png'}
              alt="Long-form Interview Process"
              className="w-full rounded-lg shadow-lg mb-4"
            />
            <h3 className="text-xl font-semibold mb-2">1. Long-form Interviews Capture Individual Nuances</h3>
            <p>
              Each AI Twin begins with an extensive, personalized interview. We don't rely on simple demographic data or surface-level preferences. Instead, we conduct deep conversations that explore values, experiences, decision-making patterns, and the subtle attitudes that make each person unique.
            </p>
          </div>

          {/* Step 2: Create AI Twin */}
          <div className="mb-8">
            <img 
              src={process.env.PUBLIC_URL + '/img/AI Twin Project/ChatGPT Image Jul 11, 2025, 06_36_43 PM.png'}
              alt="Creating the AI Twin"
              className="w-full rounded-lg shadow-lg mb-4"
            />
            <h3 className="text-xl font-semibold mb-2">2. AI Twin Creation</h3>
            <p>
              Using advanced language models and our proprietary methodology, we create an AI agent that embodies the interview participant's personality, preferences, and decision-making style. This isn't a simple chatbot—it's a sophisticated digital twin that can reason and respond like the original person.
            </p>
          </div>

          {/* Step 3: Weekly Check-ins */}
          <div className="mb-8">
            <img 
              src={process.env.PUBLIC_URL + '/img/AI Twin Project/ChatGPT Image Jul 11, 2025, 06_36_48 PM.png'}
              alt="Weekly Check-ins with AI Twin"
              className="w-full rounded-lg shadow-lg mb-4"
            />
            <h3 className="text-xl font-semibold mb-2">3. Weekly Check-ins Keep Twins Current</h3>
            <p>
              People change, and so should their AI twins. Through regular check-ins, we ensure that our AI agents stay synchronized with their real-world counterparts. As attitudes shift and new experiences shape perspectives, the AI twin evolves accordingly.
            </p>
          </div>

          {/* Step 4: Pay Royalty */}
          <div className="mb-8">
            <img 
              src={process.env.PUBLIC_URL + '/img/AI Twin Project/ChatGPT Image Jul 12, 2025, 06_16_48 PM.png'}
              alt="Participants Receive Pay Royalty"
              className="w-full rounded-lg shadow-lg mb-4"
            />
            <h3 className="text-xl font-semibold mb-2">4. Participants Receive Fair Compensation</h3>
            <p>
              We believe in ethical AI development. Participants not only contribute to groundbreaking research but are fairly compensated for their ongoing contribution to the digital twin ecosystem. This creates a sustainable, ethical model that respects and values human input.
            </p>
          </div>

          <h2 className="text-2xl font-bold mt-8 mb-4">Our Methodology</h2>
          <ul className="space-y-2">
            <li><strong>Monthly human validation</strong> ensures responses stay accurate and out-of-sample valid</li>
            <li><strong>Strict privacy and consent controls</strong> put participants in charge of their data</li>
            <li><strong>Continuous model improvement</strong> based on real-world feedback and validation</li>
            <li><strong>Demographic representation</strong> ensures diverse and inclusive AI twin populations</li>
          </ul>

          <h2 className="text-2xl font-bold mt-8 mb-4">Why It Matters</h2>
          <p>
            Traditional surveys are slow, expensive, and plagued by declining response rates and misrepresentation. With AI Twins, researchers can simulate fieldwork, segment audiences, and test messaging—all with speed, scale, and accuracy not possible with legacy methods.
          </p>

          <div className="bg-gray-900/50 border border-gray-700 rounded-lg p-6 my-8">
            <h3 className="text-lg font-semibold text-white mb-2">Key Benefits:</h3>
            <ul className="space-y-1 text-gray-300">
              <li>• <strong>Speed:</strong> Get insights in hours, not weeks</li>
              <li>• <strong>Scale:</strong> Survey thousands of AI twins simultaneously</li>
              <li>• <strong>Cost:</strong> Fraction of traditional research costs</li>
              <li>• <strong>Accuracy:</strong> Respondent-level precision backed by validation</li>
              <li>• <strong>Ethics:</strong> Fair compensation and consent-driven participation</li>
            </ul>
          </div>

          <h2 className="text-2xl font-bold mt-8 mb-4">The Bottom Line</h2>
          <p className="text-lg">
            AI Twins deliver respondent-level accuracy at a fraction of the time and cost, while setting a new bar for transparency and methodological rigor in synthetic research.
          </p>

          <p>
            This isn't just about replacing traditional surveys—it's about unlocking new possibilities for understanding human behavior, testing ideas, and making better decisions based on deeper insights.
          </p>

          <div className="bg-gradient-to-r from-pink/20 to-blue/20 border border-pink/30 rounded-lg p-6 my-8">
            <h3 className="text-lg font-semibold text-white mb-2">Ready to Experience AI Twins?</h3>
            <p className="text-gray-300 mb-4">
              Visit the AI Twin Project to learn more about our methodology and see real examples of AI twins in action.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a 
                href="https://aitwinproject.com/" 
                target="_blank" 
                rel="noopener noreferrer"
                className="px-6 py-3 bg-gradient-to-r from-pink to-blue text-white font-semibold rounded-lg transition duration-300 hover:opacity-90 text-center"
              >
                Visit AI Twin Project →
              </a>
              <a 
                href="#waitlist" 
                onClick={(e) => {
                  e.preventDefault();
                  navigate('/');
                  setTimeout(() => {
                    const waitlistSection = document.getElementById('waitlist');
                    if (waitlistSection) {
                      waitlistSection.scrollIntoView({ behavior: 'smooth' });
                    }
                  }, 100);
                }}
                className="px-6 py-3 border border-gray-600 text-white font-semibold rounded-lg transition duration-300 hover:bg-gray-800 text-center"
              >
                Join Our Waitlist
              </a>
            </div>
          </div>

          <p className="mt-8 font-semibold text-gray-300">— Sharif From HumanAI</p>

          <div className="bg-gray-900 border border-gray-700 rounded-lg p-6 mt-8">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <div>
                <h3 className="text-xl font-bold text-white mb-2">Ready to Try Synthetic Respondents?</h3>
                <p className="text-gray-300">Experience the future of market research with HumanAI's synthetic respondent technology.</p>
              </div>
              <a 
                href="https://www.humanaienterprise.com/auth" 
                target="_blank" 
                rel="noopener noreferrer"
                className="bg-pink-600 hover:bg-pink-700 text-white font-semibold py-3 px-6 rounded-lg transition duration-200 whitespace-nowrap"
              >
                Use It Free
              </a>
            </div>
          </div>

        </div>
      </div>
    </div>
  );
};

export default AITwinProject; 